/**
 * PendingAnnouncementsWidget — Dashboard banner listing announcements
 * the current user must acknowledge. Hidden when everything is signed off.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Megaphone, ArrowRight, AlertCircle } from "lucide-react";


export default function PendingAnnouncementsWidget() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/announcements/pending");
        if (!cancelled) setRows(r.data || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!rows || rows.length === 0) return null;

  return (
    <Card
      className="p-4 rounded-lg border border-purple-300 bg-purple-50 shadow-none"
      data-testid="dashboard-announcements-pending"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-purple-900">
          <AlertCircle className="h-4 w-4" />
          <div>
            <div className="text-sm font-semibold">
              {rows.length} announcement{rows.length === 1 ? "" : "s"} to acknowledge
            </div>
            <div className="text-2xs text-purple-800">
              New messages from management that need your sign-off.
            </div>
          </div>
        </div>
        <Link
          to="/learning?tab=announcements"
          className="text-xs text-purple-900 hover:text-purple-950 inline-flex items-center gap-1 font-medium"
          data-testid="dashboard-announcements-see-all"
        >
          Open announcements <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <ul className="space-y-1">
        {rows.slice(0, 5).map((a) => (
          <li key={a.id} className="flex items-center gap-2 text-sm" data-testid={`dashboard-announcement-pending-${a.id}`}>
            <Megaphone className="h-3.5 w-3.5 text-purple-700 shrink-0" />
            <Link
              to="/learning?tab=announcements"
              className="flex-1 truncate hover:underline text-purple-950"
            >
              {a.title}
            </Link>
            {a.category && (
              <span className="text-2xs uppercase tracking-wider text-purple-800">{a.category}</span>
            )}
          </li>
        ))}
        {rows.length > 5 && (
          <li className="text-2xs text-purple-800 pt-1">…and {rows.length - 5} more</li>
        )}
      </ul>
    </Card>
  );
}
